/**
 * Academic structure API (issue #11) — departments, programs, semesters.
 *
 * Typed wrappers over the shared `api` client, mirroring students.ts's shape.
 * Reads are open to any active tenant member; writes are admin-only (enforced
 * server-side by RLS + `verify_admin` — this client makes no authority claim).
 */
import { commitOptions } from "./commitSeams";
import { api } from "@/lib/api/client";

export type ActiveStatus = "active" | "inactive";
export type SemesterStatus = "active" | "upcoming" | "closed";
export type CohortStatus = "active" | "archived";

export interface StructureImpact { section_count?: number; enrollment_count?: number }

export interface Department extends StructureImpact {
  id: string;
  name: string;
  description: string | null;
  lead_user_id: string | null;
  status: ActiveStatus;
  /**
   * Programs filed under this department, and therefore whether it can be
   * removed at all. Deletion is guarded server-side; this is what lets the
   * surface say so before the click rather than after it (#484).
   */
  program_count: number;
  created_at: string;
  updated_at: string;
}

export interface CreateDepartment {
  name: string;
  description?: string | null;
  lead_user_id?: string | null;
  status?: ActiveStatus;
}

export interface PatchDepartment {
  expected_updated_at?: string;
  name?: string;
  description?: string | null;
  lead_user_id?: string | null;
  status?: ActiveStatus;
}

export interface Semester extends StructureImpact {
  id: string;
  name: string;
  start_date: string | null;
  end_date: string | null;
  status: SemesterStatus;
  grades_due_date: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateSemester {
  name: string;
  start_date?: string | null;
  end_date?: string | null;
  status?: SemesterStatus;
  grades_due_date?: string | null;
}

export interface PatchSemester {
  expected_updated_at?: string;
  name?: string;
  start_date?: string | null;
  end_date?: string | null;
  status?: SemesterStatus;
  grades_due_date?: string | null;
}

export interface TermSeasonSection {
  course_id: string;
  code: string;
  title: string;
  status: string;
  href: string;
  active_enrollments: number;
  distinct_enrolled_learners: number;
  teaching_team: { user_id: string | null; name: string; kind: "primary" | "co_instructor" }[];
  has_active_primary_instructor: boolean;
}

export interface TermSeasonReviewFact {
  course_id: string;
  course_code: string;
  course_title: string;
  href: string;
  responsible_teaching_team: TermSeasonSection["teaching_team"];
  session_id?: string;
  assignment_id?: string;
  assignment_title?: string;
  enrollment_id?: string;
  missing_count?: number;
  display_name?: string;
  /** Learners counted on one piece of work. Coursework reaches administration as counts only. */
  count?: number;
}

export interface TermSeasonProjection {
  version: "term-season-v2";
  term: Semester & {
    grades_due_relationship: { state: "not_set" | "upcoming" | "due_today" | "overdue"; days: number | null };
  };
  season: {
    section_count: number;
    sections_by_state: { draft: number; published: number; archived: number };
    active_enrollment_count: number;
    distinct_enrolled_learner_count: number;
    published_with_active_primary_instructor: number;
    published_missing_active_primary_instructor: number;
    sections: TermSeasonSection[];
  };
  close_review: {
    published_sections_remain_published: TermSeasonReviewFact[];
    scheduled_sessions: TermSeasonReviewFact[];
    missing_attendance: TermSeasonReviewFact[];
    handed_in_not_released: TermSeasonReviewFact[];
    returned_awaiting_resubmission: TermSeasonReviewFact[];
    unproposed_results: TermSeasonReviewFact[];
    pending_certification: TermSeasonReviewFact[];
  };
}

export interface Program extends StructureImpact {
  id: string;
  department_id: string | null;
  department_name: string | null;
  name: string;
  name_ar: string | null;
  code: string | null;
  description: string | null;
  duration_label: string | null;
  status: ActiveStatus;
  created_at: string;
  updated_at: string;
}

export interface CreateProgram {
  department_id?: string | null;
  name: string;
  name_ar?: string | null;
  code?: string | null;
  description?: string | null;
  duration_label?: string | null;
  status?: ActiveStatus;
}

export interface PatchProgram {
  expected_updated_at?: string;
  department_id?: string | null;
  name?: string;
  name_ar?: string | null;
  code?: string | null;
  description?: string | null;
  duration_label?: string | null;
  status?: ActiveStatus;
}

export interface Cohort extends StructureImpact {
  id: string;
  program_id: string | null;
  program_name: string | null;
  name: string;
  entry_year: number;
  label: string;
  status: CohortStatus;
  archived_at: string | null;
  archived_by: string | null;
  student_count: number;
  active_student_count: number;
  roster_batch_count: number;
  in_active_use: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateCohort {
  program_id?: string | null;
  name: string;
  entry_year: number;
  label: string;
}

export interface PatchCohort {
  expected_updated_at?: string;
  program_id?: string | null;
  name?: string;
  entry_year?: number;
  label?: string;
  status?: CohortStatus;
}

export interface CohortFilters {
  q?: string;
  program_id?: string;
  status?: CohortStatus | "all";
}

export const academicStructureApi = {
  getDepartments: () => api.get<Department[]>("/departments"),
  createDepartment: (body: CreateDepartment, key: string) => api.post<Department>("/departments", body, commitOptions(key)),
  patchDepartment: (id: string, body: PatchDepartment, key: string) =>
    api.patch<Department>(`/departments/${id}`, body, commitOptions(key)),
  deleteDepartment: (id: string, version: string, key: string) => api.delete<void>(`/departments/${id}?${new URLSearchParams({ expected_updated_at: version })}`, undefined, commitOptions(key)),

  getSemesters: () => api.get<Semester[]>("/semesters"),
  getSemesterSeason: (id: string) => api.get<TermSeasonProjection>(`/semesters/${id}/season`),
  createSemester: (body: CreateSemester, key: string) => api.post<Semester>("/semesters", body, commitOptions(key)),
  patchSemester: (id: string, body: PatchSemester, key: string) => api.patch<Semester>(`/semesters/${id}`, body, commitOptions(key)),

  getPrograms: () => api.get<Program[]>("/programs"),
  createProgram: (body: CreateProgram, key: string) => api.post<Program>("/programs", body, commitOptions(key)),
  patchProgram: (id: string, body: PatchProgram, key: string) => api.patch<Program>(`/programs/${id}`, body, commitOptions(key)),

  getCohorts: (filters: CohortFilters = {}) => {
    const params = new URLSearchParams();
    if (filters.q) params.set("q", filters.q);
    if (filters.program_id) params.set("program_id", filters.program_id);
    params.set("status", filters.status ?? "active");
    return api.get<Cohort[]>(`/cohorts?${params.toString()}`);
  },
  createCohort: (body: CreateCohort, key: string) => api.post<Cohort>("/cohorts", body, commitOptions(key)),
  patchCohort: (id: string, body: PatchCohort, key: string) => api.patch<Cohort>(`/cohorts/${id}`, body, commitOptions(key)),
};
